'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { Loader2 } from 'lucide-react';
interface GoogleLoginButtonProps {
  callbackURL?: string;
  onError?: (message: string) => void;
  variant?: 'large' | 'round';
}

/**
 * Кнопка входа через Google OAuth.
 * Использует встроенный social-провайдер Better Auth.
 *
 * variant='large' — полноширинная кнопка с текстом (используется в стандартной форме).
 * variant='round' — круглая иконка 48×48 для компактного ряда провайдеров.
 */
export function GoogleLoginButton({
  callbackURL = '/',
  onError,
  variant = 'large'
}: GoogleLoginButtonProps) {
  const [loading, setLoading] = useState(false);
  const handleLogin = async () => {
    setLoading(true);
    try {
      const result = await authClient.signIn.social({
        provider: 'google',
        callbackURL
      });
      // If client didn't auto-redirect, handle it manually
      if (result?.data?.url) {
        window.location.href = result.data.url;
      } else if (result?.error) {
        console.error('[GoogleLogin] API error:', result.error);
        onError?.(result.error.message || 'Ошибка авторизации через Google');
        setLoading(false);
      } else {
        console.error('[GoogleLogin] Unexpected result:', JSON.stringify(result));
      }
    } catch (err) {
      console.error('[GoogleLogin] Error:', err);
      onError?.('Ошибка авторизации через Google');
      setLoading(false);
    }
  };
  if (variant === 'round') {
    return <button type="button" onClick={handleLogin} disabled={loading} aria-label="Войти через Google" className="flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 disabled:opacity-50 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-zinc-700 transition-colors cursor-pointer disabled:cursor-not-allowed">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon className="w-[31px] h-[31px]" />}
      </button>;
  }
  return <button onClick={handleLogin} disabled={loading} className="flex items-center justify-center gap-3 w-full h-[52px] px-5 bg-white hover:bg-slate-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 disabled:opacity-50 text-slate-800 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 rounded-2xl text-[15px] font-medium tracking-tight shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:border-slate-300 dark:hover:border-zinc-600 transition-all cursor-pointer disabled:cursor-not-allowed">
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <GoogleIcon className="w-5 h-5" />}
      Войти через Google
    </button>;
}
function GoogleIcon(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />;
    t2 = <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />;
    t3 = <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />;
    t4 = <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== className) {
    t5 = <svg className={className} viewBox="0 0 24 24">{t1}{t2}{t3}{t4}</svg>;
    $[4] = className;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}